/**
 * calibreResolution — shared, brand-scope-aware helpers for resolving a
 * reference's free-text `calibre` string to a custom_calibres entry.
 *
 * Four rules, applied consistently by every client-side resolver (and mirrored
 * server-side in functions/src/deriveManufacturerBrands.ts):
 *
 *  1. Brand-scoped AKAs. An `aliasDetails` entry with a `brandId` only matches
 *     references belonging to that brand. AKAs with no scope — and any alias
 *     present only in the legacy flat `alsoKnownAs` array — are universal
 *     (match any brand). Canonical names are always universal. This prevents a
 *     brand-scoped catch-all alias (e.g. a Laco-scoped "Unspecified Quartz")
 *     from matching another brand's references and polluting derived data.
 *
 *  2. Placeholder blacklist. Known generic/catch-all strings never resolve —
 *     belt-and-braces noise suppression even when a bad AKA slips through.
 *
 *  3. Catch-all calibres. A calibre flagged `data.isCatchAll` is a junk bucket
 *     for generic strings, so it never matches by anything UNIVERSAL — not its
 *     canonical name, not an unscoped AKA, not a legacy flat `alsoKnownAs`
 *     entry. A BRAND-SCOPED AKA still matches its own brand's references: the
 *     scope already does the safety work the flag exists to provide, and the
 *     alias cannot reach any other brand to pollute it. "Jacob & Co. Swiss
 *     Quartz" carrying a Jacob-scoped `Swiss Quartz` is the case — the refs it
 *     was created for must resolve to it.
 *
 *     The flag is named for what it blocks: brand DERIVATION. That stays
 *     blocked outright, catch-all calibres contributing nothing to a
 *     manufacturer's derived brand set — enforced server-side in
 *     deriveManufacturerBrands (on both the `calibreId` FK path and the string
 *     path, so a ref LINKED to a catch-all still derives nothing) and
 *     re-asserted by the one client surface that mirrors that derivation,
 *     ManufacturerBrandDrillDown. Matching for enrichment is the looser
 *     question and is answered here.
 *
 *  4. Deprecated calibres. A calibre flagged `deprecated` has been soft-deleted
 *     — reclassified into a brand-specific tier of another calibre, with a
 *     permanent redirect — so it matches NOTHING. Not its canonical name, not an
 *     alias of any kind, and unlike rule #3 there is no brand-scoped escape
 *     hatch: a soft-delete that stays reachable by name is not a delete, it is a
 *     second resolution path pointing at a doc no one may pick any more. The
 *     names it used to answer to are moved onto the redirect target by
 *     migrateStandaloneToTier, which is what keeps those strings resolving.
 */

import type { CalibreAliasDetail, CalibreData } from './firebase';
import { calibreVisibleToBrand, type CalibreBrandRefs } from './calibreClassification';

/**
 * Generic / catch-all calibre strings that must never resolve to a real
 * calibre for derivation purposes. Compared against a normalized (trimmed,
 * whitespace-collapsed, lower-cased) value. Kept intentionally tight — the
 * correct semantic is rule #1 (brand-scoped AKAs); this is a safety net for
 * refs whose `calibre` field carries a movement-type placeholder rather than a
 * real calibre identity.
 *
 * Keep in sync with PLACEHOLDER_CALIBRE_STRINGS in
 * functions/src/deriveManufacturerBrands.ts.
 */
export const PLACEHOLDER_CALIBRE_STRINGS: ReadonlySet<string> = new Set([
  'unspecified',
  'unspecified quartz',
  'unspecified automatic',
  'unspecified manual',
  'unspecified mechanical',
  'unknown',
  'unknown movement',
  'unknown calibre',
  'unknown caliber',
  'quartz',
  'quartz movement',
  'automatic',
  'automatic movement',
  'manual',
  'manual winding',
  'mechanical',
  'n/a',
  'none',
  'tbd',
]);

/**
 * Proper nouns that must survive the American/British spelling fold below.
 * "Caliber Corner" is the scraper source (functions/src/index.ts builds URL
 * slugs from it) — folding it to "Calibre Corner" would break those lookups.
 * Tested against the RAW input, before normalization.
 */
const CALIBER_SPELLING_FOLD_EXEMPT = /caliber corner/i;

/**
 * Unicode dash characters that fold onto ASCII HYPHEN-MINUS (U+002D) before any
 * comparison: U+2010 HYPHEN, U+2011 NON-BREAKING HYPHEN, U+2012 FIGURE DASH,
 * U+2013 EN DASH, U+2014 EM DASH, U+2015 HORIZONTAL BAR and U+2212 MINUS SIGN.
 *
 * NFC does NOT do this — it leaves every one of them untouched, so a spec sheet
 * pasted as "CH 29‑535 PS Q" (U+2011) and a hand-typed "CH 29-535 PS Q" hashed
 * to two different keys: the duplicate-alias gate never fired and the importer
 * minted a second calibre document. NFKC is not the fix either — it maps U+2011
 * onto U+2010, still not onto ASCII. The fold has to be explicit.
 */
const UNICODE_DASHES = /[\u2010-\u2015\u2212]/g;

/**
 * Fold every Unicode dash in `value` onto ASCII "-". Exported so the calibre
 * document-id slug builder applies exactly the same fold as the comparison key
 * — the two drifting apart is what produced `ch-29535-ps-q` (U+2011 stripped as
 * "not alphanumeric") alongside `ch-29-535-ps-q` for one movement.
 */
export function foldUnicodeDashes(value: string): string {
  return value.replace(UNICODE_DASHES, '-');
}

/**
 * Normalize a calibre string: trim, collapse whitespace, lower-case, fold every
 * Unicode dash onto ASCII "-", fold the American spelling onto the British one
 * ("caliber" → "calibre") so the two are interchangeable in every calibre
 * comparison, then Unicode-NFC the result.
 *
 * The NFC pass is what makes accented calibres ("Raúl Pagès Calibre RP1",
 * "Habring²") match at all: a decomposed "u" + combining-acute from one source
 * is a different byte string to a composed "ú" from another, so a byte
 * comparison silently reports "unmatched" for two visually identical names. It
 * runs LAST because `toLowerCase()` can itself emit decomposed output. NFC is
 * the identity for pure-ASCII input, so nothing else changes.
 *
 * This is a COMPARISON key only — never use it to build a document id or any
 * other storage key, or existing `...-caliber-...` docs will no longer resolve
 * to themselves.
 *
 * Keep in sync with normalizeCalibreKey in functions/src/calibreKey.ts.
 */
export function normalizeCalibreKey(value: string): string {
  const normalized = foldUnicodeDashes(value)
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  const folded = CALIBER_SPELLING_FOLD_EXEMPT.test(value)
    ? normalized
    : normalized.replace(/\bcaliber(s?)\b/g, 'calibre$1');
  return folded.normalize('NFC');
}

/** True when the (already-or-not normalized) value is a known placeholder. */
export function isPlaceholderCalibreString(value: string): boolean {
  return PLACEHOLDER_CALIBRE_STRINGS.has(normalizeCalibreKey(value));
}

/** The subset of a calibre entry the resolvers inspect. */
export interface ResolvableCalibre {
  name: string;
  alsoKnownAs?: string[];
  aliasDetails?: CalibreAliasDetail[];
  /**
   * Soft-delete flag. Absent on almost every calibre, and a corpus loaded
   * without it reads as "not deprecated" — which is why the loaders that CAN
   * see deprecated docs must project it (see backfillCalibreLinks.loadCalibres).
   */
  deprecated?: boolean;
  data?: Pick<CalibreData, 'isCatchAll'>;
}

/**
 * True when the calibre is a catch-all placeholder — a junk bucket for generic
 * strings rather than a real movement.
 *
 * Two different rules read this. For MATCHING it removes only the universal
 * names ({@link calibreMatchKeysForBrand}); for brand DERIVATION it removes the
 * calibre entirely, which is what the flag's own label promises, so a surface
 * mirroring the server's derivation calls this directly to skip the calibre
 * outright rather than leaning on the match keys to come back empty.
 */
export function isCatchAllCalibre(entry: ResolvableCalibre): boolean {
  return entry.data?.isCatchAll === true;
}

/**
 * True when the calibre has been soft-deleted — reclassified into a tier of
 * another calibre, with `redirectToCalibreId` naming where its data now lives.
 *
 * Read by both match functions, and by neither for the same reason as
 * {@link isCatchAllCalibre}: a catch-all is a REAL calibre whose universal names
 * are too generic to trust, so its brand-scoped aliases survive. A deprecated
 * calibre is not a destination at all, so every name it carries drops — see
 * rule #4 in the module header.
 */
export function isDeprecatedCalibre(entry: ResolvableCalibre): boolean {
  return entry.deprecated === true;
}

/**
 * The normalized key for a calibre's CANONICAL name, or `null` when it has none
 * worth matching on (catch-all, empty, or a placeholder string).
 *
 * The catch-all exclusion is unconditional here and has no brand-scoped escape
 * hatch: a canonical name is universal by definition, so a catch-all's own name
 * is exactly the generic string the flag exists to keep out of every brand.
 *
 * A canonical name is universal: it names the movement itself, not a house
 * nickname for it, so it matches a reference from ANY brand. Brands routinely
 * fit base calibres they did not make — a Bulova powered by a Sellita SW200, a
 * Longines by an ETA — and the ref's `calibre` string then spells out the
 * maker's own name for it. Scoping that away is what left "Sellita Calibre
 * SW200" unmatched on a Bulova import: the calibre was discarded for belonging
 * to Sellita before the two identical strings were ever compared.
 *
 * Brand scope narrows ALIASES only, where it is doing real disambiguation work
 * (whose "2035" is this?), not canonical names, where there is nothing to
 * disambiguate.
 */
export function calibreCanonicalMatchKey(entry: ResolvableCalibre): string | null {
  if (isDeprecatedCalibre(entry)) return null;
  if (isCatchAllCalibre(entry)) return null;
  const canonical = normalizeCalibreKey(entry.name || '');
  if (!canonical || PLACEHOLDER_CALIBRE_STRINGS.has(canonical)) return null;
  return canonical;
}

/**
 * The set of normalized keys (canonical name + in-scope AKAs) that a reference
 * from `refBrandId` may match this calibre by.
 *
 * - Placeholder strings are excluded from the key set.
 * - A brand-scoped AKA is included only when its `brandId` equals `refBrandId`.
 * - Unscoped AKAs, legacy flat `alsoKnownAs`, and the canonical name are
 *   universal (always included).
 * - A DEPRECATED calibre keeps nothing: the key set is empty, so no string
 *   reaches it by any route. This is the one rule with no scoped exception.
 * - A catch-all calibre keeps ONLY its brand-scoped AKAs: every universal name
 *   drops out, so it can never claim a string from a brand that did not scope
 *   an alias to itself. The scoped rows survive because the scope has already
 *   confined them to one brand, which is the same protection the flag provides
 *   — refusing them protected nobody and left the refs the calibre was created
 *   for reading as unmatched.
 *
 * One alias NAME may carry several `aliasDetails` rows, each scoped to a
 * different brand: "Miyota Calibre 2035" answers to the bare string `2035`
 * under Bulova AND under Caravelle, because both houses print the short form.
 * Every row for a name is therefore consulted — the name is in scope when ANY
 * of them admits this brand.
 */
export function calibreMatchKeysForBrand(
  entry: ResolvableCalibre,
  refBrandId?: string,
): string[] {
  // Rule #4, ahead of everything: a soft-deleted calibre answers to no name.
  // Returning early rather than filtering per-name is what makes it impossible
  // for a brand-scoped alias to slip through the way one does for a catch-all.
  if (isDeprecatedCalibre(entry)) return [];

  const catchAll = isCatchAllCalibre(entry);

  const keys = new Set<string>();

  // Already `null` for a catch-all — see calibreCanonicalMatchKey.
  const canonical = calibreCanonicalMatchKey(entry);
  if (canonical) keys.add(canonical);

  // Map each aliasDetail name → EVERY brand scope it was granted (undefined =
  // universal). A plain name → scope map collapses duplicates to whichever row
  // happened to come last in the array, which silently hid every earlier
  // brand's alias — `2035` scoped to both Bulova and Caravelle resolved for
  // Caravelle alone, and re-ordering the rows moved the failure to the other
  // brand.
  const scopesByName = new Map<string, Set<string | undefined>>();
  for (const detail of entry.aliasDetails ?? []) {
    const name = normalizeCalibreKey(detail?.name || '');
    if (!name) continue;
    const scopes = scopesByName.get(name) ?? new Set<string | undefined>();
    scopes.add((detail?.brandId || '').trim() || undefined);
    scopesByName.set(name, scopes);
  }

  // Union of aliasDetails names + legacy flat alsoKnownAs (latter = universal).
  const aliasNames = new Set<string>(scopesByName.keys());
  for (const alias of entry.alsoKnownAs ?? []) {
    const name = normalizeCalibreKey(alias || '');
    if (name) aliasNames.add(name);
  }

  for (const name of aliasNames) {
    // The placeholder blacklist outranks brand scope, on a catch-all as
    // anywhere else: a Jacob-scoped alias literally named "Quartz" is still the
    // noise this list exists to swallow. Only strings absent from it — "Swiss
    // Quartz" — reach the scope rule below.
    if (PLACEHOLDER_CALIBRE_STRINGS.has(name)) continue;
    const scopes = scopesByName.get(name);
    // A single universal row (undefined) opens the name to every brand, which
    // is also how a name present only in the flat array behaves.
    const universal = !scopes || scopes.has(undefined);
    const scopedToRefBrand = Boolean(refBrandId && scopes?.has(refBrandId));
    // A catch-all admits scoped rows only, so a universal name never carries it
    // into a brand. Everywhere else, universal OR in-scope admits the name and
    // only an alias scoped exclusively to other brands is refused.
    if (catchAll ? !scopedToRefBrand : !universal && !scopedToRefBrand) continue;
    keys.add(name);
  }

  return [...keys];
}

/**
 * True when `calibreValue` (a reference's free-text calibre string) resolves to
 * `entry` for a reference belonging to `refBrandId`, honouring all three rules.
 */
export function calibreEntryMatchesRef(
  entry: ResolvableCalibre,
  calibreValue: string,
  refBrandId?: string,
): boolean {
  const key = normalizeCalibreKey(calibreValue);
  if (!key || PLACEHOLDER_CALIBRE_STRINGS.has(key)) return false;
  return calibreMatchKeysForBrand(entry, refBrandId).includes(key);
}

/** Why a candidate alias was rejected as a duplicate of something on the calibre. */
export interface DuplicateAliasHit {
  /** `'canonical'` = it IS the calibre's own name; `'alias'` = an alias already carries it. */
  reason: 'canonical' | 'alias';
  /** The existing name/alias string it duplicates (original casing). */
  existing: string;
}

/**
 * True when a candidate alias scoped to `candidateBrandId` genuinely collides
 * with an existing token scoped to `existingBrandId`.
 *
 * Mirrors the scope rule in checkCalibreConflict (services/firebase.ts): only
 * two aliases scoped to genuinely DIFFERENT brands escape a collision, because
 * an unscoped alias matches every brand.
 */
function aliasScopesCollide(
  candidateBrandId: string | undefined,
  existingBrandId: string | undefined,
): boolean {
  if (candidateBrandId && existingBrandId && candidateBrandId !== existingBrandId) return false;
  return true;
}

/**
 * Would adding `aliasName` to `entry` duplicate something already on it?
 *
 * Returns the offending existing token, or `null` when the alias is genuinely
 * new. Comparison goes through {@link normalizeCalibreKey}, so case, spacing,
 * Caliber/Calibre spelling and Unicode composition ("Pagès" decomposed vs
 * composed) are all folded — a diacritic-only difference is a duplicate, not a
 * new alias.
 *
 * `candidateBrandId` is the scope the new alias would be written with
 * (undefined = universal). An existing alias scoped to a DIFFERENT brand is not
 * a duplicate: two brands may legitimately each map the same short string to
 * this calibre. The canonical name is universal, so it always collides.
 *
 * Shared by every "add an alias" surface — the calibre editor, the
 * CalibreActionToolbar picker and the bulk enrichment screen — so all three
 * reject the same inputs.
 */
export function findDuplicateAlias(
  entry: ResolvableCalibre,
  aliasName: string,
  candidateBrandId?: string,
): DuplicateAliasHit | null {
  const key = normalizeCalibreKey(aliasName);
  if (!key) return null;

  const canonical = (entry.name || '').trim();
  if (canonical && normalizeCalibreKey(canonical) === key) {
    return { reason: 'canonical', existing: canonical };
  }

  const scope = (candidateBrandId || '').trim() || undefined;

  for (const detail of entry.aliasDetails ?? []) {
    const name = (detail?.name || '').trim();
    if (!name || normalizeCalibreKey(name) !== key) continue;
    if (!aliasScopesCollide(scope, (detail?.brandId || '').trim() || undefined)) continue;
    return { reason: 'alias', existing: name };
  }

  // Legacy flat alsoKnownAs entries carry no scope, so they are universal and
  // always collide. Skip any that a richer aliasDetails entry already covers —
  // that one was scope-checked above and deliberately let through.
  const scopedAliasKeys = new Set(
    (entry.aliasDetails ?? [])
      .map((d) => normalizeCalibreKey(d?.name || ''))
      .filter(Boolean),
  );
  for (const alias of entry.alsoKnownAs ?? []) {
    const name = (alias || '').trim();
    if (!name || normalizeCalibreKey(name) !== key) continue;
    if (scopedAliasKeys.has(key)) continue;
    return { reason: 'alias', existing: name };
  }

  return null;
}

/** User-facing reason a duplicate alias was rejected. Shared so every surface reads alike. */
export function duplicateAliasMessage(
  hit: DuplicateAliasHit,
  aliasName: string,
  calibreName: string,
): string {
  const alias = aliasName.trim();
  return hit.reason === 'canonical'
    ? `"${alias}" is already the name of this calibre`
    : `"${alias}" is already an alias of "${calibreName}" (as "${hit.existing}")`;
}

/** How a reference's calibre string linked to a calibre entry. */
export interface CalibreRefMatch {
  /** `'canonical'` = matched the calibre's own name; `'aka'` = matched an alias. */
  via: 'canonical' | 'aka';
  /** The canonical name or the alias display string that matched (original casing). */
  matchedName: string;
}

/**
 * Like {@link calibreEntryMatchesRef} but, on a match, reports WHICH way the
 * reference linked to the calibre — canonical name vs alias — and the exact
 * matched string. Returns `null` on no match. Honours the same three rules
 * (brand-scoped AKAs, placeholder blacklist, catch-all) via
 * {@link calibreMatchKeysForBrand}, so `calibreRefMatchMethod(...) !== null` is
 * equivalent to `calibreEntryMatchesRef(...)`. The match method is the signal the
 * Data Health drill-down surfaces for pollution investigation.
 */
export function calibreRefMatchMethod(
  entry: ResolvableCalibre,
  calibreValue: string,
  refBrandId?: string,
): CalibreRefMatch | null {
  const key = normalizeCalibreKey(calibreValue);
  if (!key || PLACEHOLDER_CALIBRE_STRINGS.has(key)) return null;
  // Single source of truth for scope / placeholder / catch-all rules.
  if (!calibreMatchKeysForBrand(entry, refBrandId).includes(key)) return null;

  // A catch-all never matches by its canonical name, so a key that got past the
  // gate above came from a brand-scoped alias even when the calibre's own name
  // reads identically ("Swiss Quartz" as both). Skipping this branch keeps the
  // attribution honest — BulkCalibreEnricher badges the row from `via`, and a
  // `canonical` badge on a catch-all would describe a match the rules forbid.
  const canonical = isCatchAllCalibre(entry) ? '' : normalizeCalibreKey(entry.name || '');
  if (canonical && canonical === key) {
    return { via: 'canonical', matchedName: (entry.name || '').trim() };
  }

  // AKA path — recover the alias display string that matched. Prefer rich
  // aliasDetails (they carry brand scope) then the legacy flat alsoKnownAs.
  for (const detail of entry.aliasDetails ?? []) {
    const name = (detail?.name || '').trim();
    if (!name || normalizeCalibreKey(name) !== key) continue;
    const scope = (detail?.brandId || '').trim() || undefined;
    if (scope && scope !== refBrandId) continue;
    return { via: 'aka', matchedName: name };
  }
  for (const alias of entry.alsoKnownAs ?? []) {
    const name = (alias || '').trim();
    if (name && normalizeCalibreKey(name) === key) {
      return { via: 'aka', matchedName: name };
    }
  }
  // In the match-key set but couldn't attribute a display name — report as AKA.
  return { via: 'aka', matchedName: calibreValue.trim() };
}

/**
 * Build the name → calibre index the bulk importer matches rows against.
 *
 * Two passes, and which one runs second is the whole point:
 *
 *  1. ALIASES, brand-scoped. {@link calibreVisibleToBrand} decides whether the
 *     calibre's aliases are on offer to this import at all, over its FULL brand
 *     set (primary + every brand-scoped alias); {@link calibreMatchKeysForBrand}
 *     then decides which of its names this brand may match it by, and still
 *     refuses an alias scoped to a different brand. `options`
 *     {@link CalibreMatchScopeOptions.crossBrandUnscopedAliases} drops the
 *     first of those two gates, leaving the per-name rule to decide alone.
 *  2. CANONICAL NAMES, unscoped. Every entry's own name goes in whatever brand
 *     is importing — see {@link calibreCanonicalMatchKey}. Running this pass
 *     last also settles the collision case in the right direction: an exact
 *     canonical-name match outranks some other calibre's alias on the same key.
 *
 * Within a pass, later entries win on a key collision, as before.
 */
/**
 * How wide the alias half of a match should reach across brand ownership.
 *
 * The default (`crossBrandUnscopedAliases` absent or false) is the bulk
 * importer's rule: {@link calibreVisibleToBrand} first decides whether a
 * calibre's nicknames are on offer to this brand at all, so an alias on a
 * calibre owned elsewhere is out of reach even when the alias itself carries no
 * scope. That coarse gate is what keeps an import from dragging in house
 * nicknames belonging to unrelated makers.
 *
 * With the option ON, that ownership gate is skipped and only the per-name rule
 * in {@link calibreMatchKeysForBrand} decides — so an UNSCOPED alias matches
 * whatever brand the reference belongs to, while an alias scoped to another
 * brand still refuses, exactly as it does with the gate in place. The case it
 * restores: an admin typing "Cal. 2824" over Tissot references, where
 * `Cal. 2824` is a universal AKA on the ETA-owned "ETA 2824-2".
 *
 * Only the References bulk action bar asks for this. There an admin has already
 * named the exact string and the exact selection, so the breadth is a
 * deliberate instruction rather than a guess made on their behalf.
 */
export interface CalibreMatchScopeOptions {
  /** Let an unscoped alias match regardless of which brand owns the calibre. */
  crossBrandUnscopedAliases?: boolean;
}

export function buildCalibreMatchIndex<T extends ResolvableCalibre & CalibreBrandRefs>(
  entries: readonly T[],
  brandId?: string,
  brandName?: string,
  options?: CalibreMatchScopeOptions,
): Map<string, T> {
  const idx = new Map<string, T>();
  for (const entry of entries) {
    if (
      !options?.crossBrandUnscopedAliases &&
      !calibreVisibleToBrand(entry, brandId, brandName)
    ) {
      continue;
    }
    for (const key of calibreMatchKeysForBrand(entry, brandId)) idx.set(key, entry);
  }
  for (const entry of entries) {
    const canonical = calibreCanonicalMatchKey(entry);
    if (canonical) idx.set(canonical, entry);
  }
  return idx;
}

/** A reference's free-text calibre string, resolved to an authoritative FK. */
export interface ResolvedRefCalibre<T> {
  /** `custom_calibres/{id}` — what goes in `WatchReference.calibreId`. */
  calibreId: string;
  /** Which name carried the match: the calibre's own, or one of its aliases. */
  source: 'canonical' | 'aka';
  /**
   * The matched entry. Handed back so a caller that also writes legacy specs
   * reads them off the SAME calibre the id points at — see below.
   */
  entry: T;
}

/** The calibre fields {@link resolveCalibreForRef} needs on top of the match rules. */
export type ResolvableRefCalibre = ResolvableCalibre & CalibreBrandRefs & { id: string };

/**
 * Resolve a reference's free-text `calibre` string to the calibre it names, for
 * a reference belonging to `brand`.
 *
 * This is the single entry point every write path should use to decide what
 * `WatchReference.calibreId` becomes. It resolves through
 * {@link buildCalibreMatchIndex} rather than scanning for the first entry that
 * happens to match, which matters in two ways:
 *
 *  - The index's second pass makes a canonical-name hit outrank another
 *    calibre's alias on the same key, so two calibres competing for one string
 *    resolve the same way here as they do in the bulk importer.
 *  - Returning the matched `entry` alongside the id means any legacy movement
 *    specs a caller dual-writes come off the same calibre the id points at. A
 *    caller that resolved the id one way and the specs another could write
 *    `calibreId` for one calibre next to `movement.*` values from a different
 *    one, and nothing downstream would flag the mismatch.
 *
 * Returns `null` when nothing matches — including the deliberate non-matches: a
 * placeholder string, an alias scoped to some other brand, a catch-all calibre
 * reached by anything but one of its own brand-scoped aliases, and a deprecated
 * calibre reached by anything at all. `null` means "no link", never "link to
 * something approximate".
 *
 * A ref CAN therefore link to a catch-all, via that scoped alias. Nothing
 * downstream treats the resulting `calibreId` as a derivation signal: the
 * server re-checks the flag on the FK it is handed, so the link enriches the
 * reference without feeding a manufacturer's brand set.
 *
 * `options` widens the alias half only — see {@link CalibreMatchScopeOptions}.
 * Callers that leave it unset keep the bulk importer's ownership gate, so this
 * is opt-in per write path rather than a change to what "resolve" means.
 *
 * Pure by design: the corpus is passed in rather than fetched, so callers keep
 * ownership of how they load calibres, and this is testable without Firestore.
 *
 * `source` decides nothing today — it records which name carried the match, for
 * the audit trail the migration's later phases report on.
 *
 * There is deliberately no `calibreTierId`. A tier is chosen per reference from
 * `data.tiers[]`; a calibre string and a brand id carry nothing that names one,
 * and the reference editor's own link handler does not auto-select a tier
 * either (it only clears one that the newly linked calibre does not define).
 */
export function resolveCalibreForRef<T extends ResolvableRefCalibre>(
  entries: readonly T[],
  calibreString: string,
  brand: { id?: string; name?: string },
  options?: CalibreMatchScopeOptions,
): ResolvedRefCalibre<T> | null {
  const key = normalizeCalibreKey(calibreString || '');
  if (!key || PLACEHOLDER_CALIBRE_STRINGS.has(key)) return null;

  const match = buildCalibreMatchIndex(entries, brand.id, brand.name, options).get(key);
  if (!match?.id) return null;

  // The index has already applied every rule; this call only recovers HOW the
  // key matched. Falling back to 'canonical' keeps the result non-null if the
  // index and the attribution pass ever disagree — the id is the load-bearing
  // half, and a wrong provenance label is better than a dropped link.
  const method = calibreRefMatchMethod(match, calibreString, brand.id);
  return { calibreId: match.id, source: method?.via ?? 'canonical', entry: match };
}

// ============================================================================
// READ SIDE — what a reference's calibre IS, rather than what to link it to
// ============================================================================
//
// Everything above resolves a free-text string INTO a `calibreId` (the write
// side, Phase 5a). The two helpers below go the other way: given a reference
// that may already carry the authoritative fields, work out what to show and
// which calibre it points at.
//
// This matters because Phase 5e turns off `calibre.dualWriteLegacyCalibreString`
// and newly written refs stop carrying a `calibre` string at all. Any read that
// keys off `ref.calibre` renders blank, filters to nothing, or counts zero from
// that moment on — silently, since an absent string is indistinguishable from a
// ref that genuinely has no calibre.
//
// The precedence mirrors the Algolia indexer's (watchlock
// `functions/src/refCalibreIdentity.ts`), so admin and the index agree on which
// calibre a ref belongs to. The one deliberate difference is the FK tier: the
// indexer resolves `calibreId` through an id-keyed map it builds during its own
// scan, which a display site has no equivalent of. `resolveDisplayCalibre` is
// therefore cache → legacy, and callers that DO hold the corpus reach the FK
// tier through `findRefCalibreEntry` below.

/** The calibre fields {@link resolveDisplayCalibre} reads off a reference. */
export interface DisplayCalibreSource {
  calibre?: string;
  calibreCache?: { canonicalName?: string };
}

/**
 * The calibre name to DISPLAY (or filter/group on) for a reference.
 *
 * Prefers `calibreCache.canonicalName` — the denormalized snapshot the ref-write
 * trigger builds from the linked calibre plus its tier — and falls back to the
 * legacy free-text `calibre` string. Returns `''` when the ref carries neither,
 * which is the honest answer: no calibre, rather than an empty-looking one.
 *
 * The cache wins over a legacy string that names something DIFFERENT, on
 * purpose. A ref whose link was changed without its legacy string being
 * rewritten has a stale string and a fresh cache; the cache is the one kept
 * current by the trigger, from both directions (`onReferenceWrite` and
 * `onCustomCalibreWrite`).
 */
export function resolveDisplayCalibre(ref: DisplayCalibreSource | undefined): string {
  if (!ref) return '';
  const cached = ref.calibreCache?.canonicalName?.trim();
  if (cached) return cached;
  return ref.calibre?.trim() || '';
}

/** The calibre fields {@link findRefCalibreEntry} reads off a reference. */
export interface RefCalibreLink extends DisplayCalibreSource {
  calibreId?: string;
}

/**
 * The calibre corpus entry a reference points at — FK first, name second.
 *
 * For callers that already hold the calibre corpus (the bulk enrichers), the
 * `calibreId` FK identifies the calibre EXACTLY, with none of the ambiguity the
 * name match has to arbitrate: no brand scoping, no alias precedence, no
 * placeholder filtering. When it is present and resolves, it is the answer.
 *
 * A DANGLING FK — an id whose calibre has since been deleted — falls THROUGH to
 * the name match rather than returning null, matching the indexer. The ref still
 * names a calibre in its cache or its legacy string, and a deleted link is no
 * reason to stop enriching from the calibre that name still resolves to.
 *
 * The name half goes through {@link resolveCalibreForRef}, so every rule that
 * governs linking governs this too — a placeholder string, a foreign-scoped
 * alias, a catch-all reached by the wrong name and a deprecated calibre all
 * still resolve to nothing here.
 *
 * `byId` is an optional prebuilt index. Callers resolving in a loop should pass
 * one (`buildCalibreIdIndex`) rather than paying a linear scan per reference.
 */
export function findRefCalibreEntry<T extends ResolvableRefCalibre>(
  entries: readonly T[],
  ref: RefCalibreLink,
  brand: { id?: string; name?: string },
  options?: CalibreMatchScopeOptions & { byId?: ReadonlyMap<string, T> },
): T | null {
  const id = ref.calibreId?.trim();
  if (id) {
    const hit = options?.byId ? options.byId.get(id) : entries.find((e) => e.id === id);
    if (hit) return hit;
  }

  const name = resolveDisplayCalibre(ref);
  if (!name) return null;
  return resolveCalibreForRef(entries, name, brand, options)?.entry ?? null;
}

/**
 * Index a calibre corpus by document id, for {@link findRefCalibreEntry}'s FK
 * tier. Every entry is stored unconditionally — membership in this map IS the
 * dangling-FK test, which is what separates "the calibre exists" from "the
 * calibre was deleted".
 */
export function buildCalibreIdIndex<T extends { id: string }>(
  entries: readonly T[],
): Map<string, T> {
  const byId = new Map<string, T>();
  for (const entry of entries) {
    if (entry.id) byId.set(entry.id, entry);
  }
  return byId;
}
